/**
 * RebookBrowseStep — the rebook drawer's "Browse" screen (BON-146): the
 * full-page calendar of destinations for this booking.
 *
 * Its own component because it REPLACES the drawer rather than nesting inside
 * it — the calendar takes the whole viewport — and because that makes the
 * drawer's three screens three named things instead of one long branch. It
 * translates the booking and the loaded targets into what the shared picker
 * needs; the picker itself is the same one the create-booking flow renders.
 */
import type { BookingWorkshop } from '../../api/bookingsV2';

import type { PickerViewMode } from './shared/pickerViewMode';
import type { PickerWorkshop } from './shared/pickerWorkshop';
import { RebookWorkshopPicker } from './shared/RebookWorkshopPicker';
import type { useRebookTargets } from './shared/useRebookTargets';

export interface RebookBrowseStepProps {
  /** The workshop being left — named in the header so the CSR can judge how far
   *  each candidate moves the customer. */
  workshop: BookingWorkshop;
  targets: ReturnType<typeof useRebookTargets>;
  month: Date;
  onMonthChange: (month: Date) => void;
  viewMode: PickerViewMode;
  onViewModeChange: (mode: PickerViewMode) => void;
  /** Back to the drawer without choosing. */
  onBack: () => void;
  onPick: (workshop: PickerWorkshop) => void;
}

export function RebookBrowseStep({
  workshop,
  targets,
  month,
  onMonthChange,
  viewMode,
  onViewModeChange,
  onBack,
  onPick,
}: RebookBrowseStepProps) {
  return (
    <RebookWorkshopPicker
      workshops={targets.workshops}
      loading={targets.loading}
      error={targets.error}
      truncated={targets.envelope?.truncated ?? false}
      regionName={workshop.region?.name ?? null}
      sourceLabel={`${workshop.name ?? 'their workshop'} · ${workshop.venue.name}`}
      treeOptions={targets.treeOptions}
      treeSpeciesId={targets.treeSpeciesId}
      onTreeChange={targets.setTreeSpeciesId}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      month={month}
      onMonthChange={onMonthChange}
      onBack={onBack}
      // The × on a browse layer returns to the drawer rather than abandoning the
      // rebook: the CSR opened the calendar to answer one question, and closing
      // it should not throw away the reason they already typed.
      onClose={onBack}
      onPick={onPick}
    />
  );
}
